import React, { useCallback, useEffect, useMemo, useState } from "react";
import PanelDashboardLayout from "../PanelDashboardLayout";
import {
  getPromotionCohortsViaAdmin,
  getGraduationEligibleViaAdmin,
  commitGraduationViaAdmin,
  reinstateGraduatesViaAdmin,
} from "../../../utils/Api";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import SchoolIcon from "@mui/icons-material/School";
import UndoIcon from "@mui/icons-material/Undo";
import RefreshIcon from "@mui/icons-material/Refresh";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

/**
 * Graduation (pass-out) — an explicit administrative act, deliberately separate from the
 * annual promotion run.
 *
 * The annual run will not graduate a student whose backlogs it cannot verify from result
 * data, which is the normal state here: the university publishes final-year results long
 * after the cohort has physically left. This screen lets the office record the pass-out
 * anyway, while showing exactly what is and is not known about each student — an unknown
 * backlog is never presented as a cleared one.
 */
const Graduation = () => {
  const [cohorts, setCohorts] = useState([]);
  const [cohortId, setCohortId] = useState("");
  const [includeNonFinalYear, setIncludeNonFinalYear] = useState(false);

  const [data, setData] = useState({ students: [], total: 0, withBacklogs: 0, withoutResultData: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [clearSelectionToggle, setClearSelectionToggle] = useState(false);

  const [confirm, setConfirm] = useState(null); // "graduate" | "reinstate"
  const [reason, setReason] = useState("");
  const [force, setForce] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const loadCohorts = useCallback(async () => {
    try {
      const res = await getPromotionCohortsViaAdmin();
      setCohorts(res?.cohorts || res || []);
    } catch (e) {
      toast.error("Failed to load cohorts");
    }
  }, []);

  const loadEligible = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getGraduationEligibleViaAdmin(cohortId || null, includeNonFinalYear);
      setData(res || { students: [] });
      setSelected([]);
      setClearSelectionToggle((t) => !t);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to load eligible students");
      setData({ students: [], total: 0, withBacklogs: 0, withoutResultData: 0 });
    } finally {
      setLoading(false);
    }
  }, [cohortId, includeNonFinalYear]);

  useEffect(() => {
    loadCohorts();
  }, [loadCohorts]);

  useEffect(() => {
    loadEligible();
  }, [loadEligible]);

  const openConfirm = (mode) => {
    if (selected.length === 0) {
      toast.warn("Select at least one student");
      return;
    }
    if (mode === "graduate" && selected.every((s) => s.alreadyGraduated)) {
      toast.warn("Every selected student has already passed out");
      return;
    }
    if (mode === "reinstate" && selected.every((s) => !s.alreadyGraduated)) {
      toast.warn("None of the selected students are graduated");
      return;
    }
    setReason("");
    setForce(false);
    setLastResult(null);
    setConfirm(mode);
  };

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required — it is written to the audit trail");
      return;
    }
    setSubmitting(true);
    try {
      const ids = selected.map((s) => s.studentId);
      const res =
        confirm === "graduate"
          ? await commitGraduationViaAdmin(ids, reason.trim(), force)
          : await reinstateGraduatesViaAdmin(ids, reason.trim());

      setLastResult(res);
      const done = confirm === "graduate" ? res.summary.graduated : res.summary.reinstated;
      if (done > 0) toast.success(`${done} student(s) updated`);
      if (res.summary.skipped > 0) toast.info(`${res.summary.skipped} skipped`);
      if (res.summary.failed > 0) toast.error(`${res.summary.failed} failed`);

      await loadEligible();
      // Keep the dialog open when anything was skipped or failed, so the reasons are
      // actually read rather than dismissed by a reload.
      if (res.summary.skipped === 0 && res.summary.failed === 0) setConfirm(null);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedStats = useMemo(() => {
    const withBacklogs = selected.filter((s) => (s.activeBacklogCount || 0) > 0).length;
    const noResults = selected.filter((s) => !s.hasResultData).length;
    const notFinalYear = selected.filter((s) => !s.isFinalYear).length;
    return { withBacklogs, noResults, notFinalYear };
  }, [selected]);

  const columns = useMemo(
    () => [
      { name: "Roll No", selector: (r) => r.rollNumber, sortable: true, width: "110px" },
      { name: "Name", selector: (r) => r.name, sortable: true, grow: 2, wrap: true },
      { name: "Cohort", selector: (r) => r.cohortName || "—", sortable: true, width: "130px" },
      { name: "Batch", selector: (r) => r.batch || "—", sortable: true, width: "120px" },
      {
        name: "Year",
        width: "110px",
        cell: (r) => (
          <span>
            {r.yearStage} ({r.yearNumber}/{r.durationYears ?? "?"})
          </span>
        ),
      },
      {
        name: "Backlogs",
        width: "130px",
        sortable: true,
        selector: (r) => r.activeBacklogCount ?? -1,
        cell: (r) => {
          if (r.activeBacklogCount == null) return <Chip size="small" label="unknown" />;
          if (r.activeBacklogCount === 0) {
            return (
              <Chip
                size="small"
                color={r.backlogVerified ? "success" : "default"}
                label={r.backlogVerified ? "0 (verified)" : "0 (unverified)"}
              />
            );
          }
          return <Chip size="small" color="error" label={`${r.activeBacklogCount} pending`} />;
        },
      },
      {
        name: "Result data",
        width: "120px",
        cell: (r) =>
          r.hasResultData ? (
            <Chip size="small" color="success" label="present" />
          ) : (
            <Chip size="small" color="warning" label="none" />
          ),
      },
      {
        name: "Status",
        width: "120px",
        cell: (r) =>
          r.alreadyGraduated ? (
            <Chip size="small" color="info" label="passed out" />
          ) : (
            <Chip size="small" label={r.lifecycleStatus} />
          ),
      },
    ],
    []
  );

  return (
    <PanelDashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
          Graduation / Pass-out
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Record a cohort as passed out without waiting for the university to release
          final-year results. Every decision is audited and can be reversed.
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>How this differs from Promotions</AlertTitle>
          The annual promotion run only graduates students whose backlogs it can confirm
          from result data, so a cohort with no results yet stays stuck in its final year.
          This screen lets you pass them out explicitly. Graduated students{" "}
          <strong>keep their portal login</strong> so they can still download marksheets.
        </Alert>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Cohort</InputLabel>
            <Select value={cohortId} label="Cohort" onChange={(e) => setCohortId(e.target.value)}>
              <MenuItem value="">All cohorts</MenuItem>
              {cohorts.map((c) => (
                <MenuItem key={c._id} value={c._id}>
                  {c.name} ({c.durationYears} yr)
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeNonFinalYear}
                onChange={(e) => setIncludeNonFinalYear(e.target.checked)}
              />
            }
            label="Also show students below final year"
          />

          <Button startIcon={<RefreshIcon />} onClick={loadEligible} disabled={loading}>
            Refresh
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            color="primary"
            startIcon={<SchoolIcon />}
            disabled={selected.length === 0}
            onClick={() => openConfirm("graduate")}
          >
            Graduate selected ({selected.length})
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            disabled={selected.length === 0}
            onClick={() => openConfirm("reinstate")}
          >
            Reinstate
          </Button>
        </Box>

        {/* Summary */}
        <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
          <Chip label={`${data.total || 0} eligible`} />
          <Chip color="error" variant="outlined" label={`${data.withBacklogs || 0} with backlogs`} />
          <Chip
            color="warning"
            variant="outlined"
            label={`${data.withoutResultData || 0} with no result data`}
          />
        </Box>

        {includeNonFinalYear && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Students below their final year are now listed. Batch names are shared across
            cohorts (for example VBA-LLB-A holds both BA-LLB-2021 and BA-LLB-2022), so check
            the Cohort column before selecting.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            columns={columns}
            data={data.students || []}
            selectableRows
            onSelectedRowsChange={({ selectedRows }) => setSelected(selectedRows)}
            clearSelectedRows={clearSelectionToggle}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100, 200]}
            highlightOnHover
            dense
            noDataComponent={
              <Box sx={{ p: 3 }}>
                <Typography variant="body2">
                  No students match. Final-year students only, unless the box above is ticked.
                </Typography>
              </Box>
            }
          />
        )}

        {/* Confirmation */}
        <Dialog open={!!confirm} onClose={() => !submitting && setConfirm(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {confirm === "graduate" ? "Graduate students" : "Reinstate students"}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {confirm === "graduate"
                ? `${selected.length} student(s) will be marked as passed out. Their current enrollment will be closed.`
                : `${selected.length} student(s) will be put back on the roll.`}
            </Typography>

            {confirm === "graduate" && (
              <>
                {selectedStats.noResults > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <strong>{selectedStats.noResults}</strong> selected student(s) have no
                    result data. You are recording a pass-out that the system cannot verify.
                  </Alert>
                )}
                {selectedStats.withBacklogs > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <strong>{selectedStats.withBacklogs}</strong> selected student(s) have
                    <em> known uncleared backlogs</em>. Graduating them contradicts recorded
                    results.
                  </Alert>
                )}
                {selectedStats.notFinalYear > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <strong>{selectedStats.notFinalYear}</strong> selected student(s) are not
                    in their final year. These are refused unless you tick the override below.
                  </Alert>
                )}
              </>
            )}

            <TextField
              label="Reason"
              placeholder="e.g. Batch passed out; university results pending"
              fullWidth
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mt: 1 }}
              required
              helperText="Recorded against every student in the audit trail."
            />

            {confirm === "graduate" && selectedStats.notFinalYear > 0 && (
              <FormControlLabel
                sx={{ mt: 1 }}
                control={<Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} />}
                label="Override: graduate students who are not in their final year"
              />
            )}

            {lastResult && (
              <Box sx={{ mt: 2 }}>
                <Alert severity={lastResult.summary.failed > 0 ? "error" : "info"}>
                  <AlertTitle>Result</AlertTitle>
                  {confirm === "graduate"
                    ? `${lastResult.summary.graduated} graduated`
                    : `${lastResult.summary.reinstated} reinstated`}
                  , {lastResult.summary.skipped} skipped, {lastResult.summary.failed} failed.
                </Alert>
                {(lastResult.skipped || []).length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 160, overflow: "auto" }}>
                    {lastResult.skipped.map((s) => (
                      <Typography key={s.studentId} variant="caption" display="block">
                        skipped {s.studentId}: {s.why}
                      </Typography>
                    ))}
                  </Box>
                )}
                {(lastResult.failed || []).length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 160, overflow: "auto" }}>
                    {lastResult.failed.map((f) => (
                      <Typography key={f.studentId} variant="caption" display="block" color="error">
                        failed {f.studentId}: {f.error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(null)} disabled={submitting}>
              Close
            </Button>
            <Button
              variant="contained"
              color={confirm === "graduate" ? "primary" : "warning"}
              onClick={submit}
              disabled={submitting || !reason.trim()}
            >
              {submitting ? "Working…" : confirm === "graduate" ? "Graduate" : "Reinstate"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PanelDashboardLayout>
  );
};

export default Graduation;
